import {Button} from '@primer/react'
import {clsx} from 'clsx'
import type {ActionabilityCategory, ActionabilityState} from './actionability/actionability-types'
import styles from './ActionabilityBanner.module.css'
import {useActionability} from './actionability/use-actionability'
import type {Channels} from '@github-ui/mergebox/use-mergeability-live-updates'
import type {NavigationUrls} from '../../types/navigation-urls-types'
import {ActionabilityBannerSkeleton} from './ActionabilityBannerSkeleton'

const CATEGORY_LABELS: Record<ActionabilityCategory, string> = {
  review: 'Your turn',
  fix: 'Action needed',
  ready: 'Ready to merge',
  merging: 'In merge queue',
  draft: 'Work in progress',
  waiting: 'No action needed',
}

const LABEL_CLASS: Record<ActionabilityCategory, string> = {
  review: styles.labelReview,
  fix: styles.labelFix,
  ready: styles.labelReady,
  merging: styles.labelMerging,
  draft: styles.labelDraft,
  waiting: styles.labelWaiting,
}

/** Handler map for action-type CTAs. Wire up real implementations here. */
type ActionHandlers = Record<string, () => void>

interface ActionabilityBannerProps {
  actionability: ActionabilityState
  urls: NavigationUrls
  /** Optional handlers for action-type CTAs (e.g. open_mergebox, mark_ready_for_review) */
  onAction?: ActionHandlers
}

export function ActionabilityBanner({
  authorLogin,
  urls,
  headSha,
  channels,
  viewerIsRequestedReviewer,
}: {
  authorLogin: string
  urls: NavigationUrls
  headSha: string
  channels: Channels | null
  viewerIsRequestedReviewer: boolean
}) {
  const {actionability, isLoading} = useActionability({
    authorLogin,
    headSha,
    channels,
    viewerIsRequestedReviewer,
  })

  if (isLoading) {
    return <ActionabilityBannerSkeleton />
  }

  if (!actionability) return null

  return <ActionabilityBannerComponent actionability={actionability} urls={urls} />
}

export function ActionabilityBannerComponent({actionability, urls, onAction}: ActionabilityBannerProps) {
  const {category, message, actionLabel, actionTarget} = actionability

  return (
    <div className={clsx(styles.banner, styles[category])} data-testid="actionability-banner">
      <p className={styles.text}>
        <span className={clsx(styles.label, LABEL_CLASS[category])}>{CATEGORY_LABELS[category]}</span> {message}
      </p>
      {actionTarget.type === 'link' ? (
        <Button as="a" href={urls[actionTarget.urlKey]} className={styles.actionButton}>
          {actionLabel}
        </Button>
      ) : (
        <Button className={styles.actionButton} onClick={() => onAction?.[actionTarget.actionId]?.()}>
          {actionLabel}
        </Button>
      )}
    </div>
  )
}
